import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuthStore } from '../store/authStore';
import { clearToken } from '../utils/tokenStorage';
import apiClient, { redirectToAuth } from '../utils/api';
import { Music2, Home, Users, Calendar, Shield, Menu, X, Heart, Bell, BookOpen, CalendarDays, ExternalLink, Settings, LogOut, MessageCircle, ChevronDown, Image, FileText, BarChart3, Search, Sparkles, Gamepad2, Radio } from 'lucide-react';
import NotificationsDropdown from './NotificationsDropdown';
import MessagesDropdown from './MessagesDropdown';
import ThemeToggle from './ThemeToggle';

// Lightweight CSS-only tooltip for icon-only nav buttons
const NavTip = ({ label, children }) => (
  <div className="relative nav-tip flex items-center">
    {children}
    <span className="nav-tip-label pointer-events-none absolute top-full left-1/2 -translate-x-1/2 mt-1.5 px-2 py-1 rounded-md bg-black/90 border border-white/10 text-[11px] text-white whitespace-nowrap opacity-0 transition-opacity duration-150 z-[70]">
      {label}
    </span>
  </div>
);

export default function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuthStore();
  const [menuOpen, setMenuOpen] = useState(false);
  const [liveCount, setLiveCount] = useState(0);
  const menuRef = useRef(null);

  // Poll live rooms count for the nav badge
  useEffect(() => {
    const fetchLive = () => {
      apiClient.get('/live/rooms')
        .then((res) => setLiveCount((res.data.rooms || []).length))
        .catch(() => {});
    };
    fetchLive();
    const t = setInterval(fetchLive, 30000);
    return () => clearInterval(t);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await apiClient.post('/auth/logout');
      clearToken();
      logout();
      setMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  // Navigation items based on auth state and role
  const getNavItems = () => {
    const items = [
      { label: 'Home', path: '/', icon: Home, show: true },
      { label: 'Browse Artists', path: '/browse', icon: Users, show: true },
      { label: 'Events', path: '/events', icon: CalendarDays, show: true },
      { label: 'Live', path: '/live', icon: Radio, show: true },
      { label: 'Blog', path: '/blog', icon: BookOpen, show: true },
    ];

    // Check if user is owner/overseer (full access) or admin (limited)
    const isOwnerOrOverseer = user?.role === 'owner' || user?.role === 'overseer';
    const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner' || user?.role === 'overseer';

    if (isAuthenticated && user) {
      // Add dashboard for all authenticated users
      items.push({ label: 'Dashboard', path: '/dashboard', icon: Calendar, show: true });
      
      // Add My Analytics for regular users/clients
      if (!isOwnerOrOverseer && user.role !== 'artist') {
        items.push({ label: 'My Analytics', path: '/my-analytics', icon: BarChart3, show: true });
        items.push({ label: 'For You', path: '/for-you', icon: Sparkles, show: true });
      }
      
      // Admin Panel - OWNERS AND OVERSEERS ONLY
      if (isOwnerOrOverseer) {
        items.push({ label: 'Admin Panel', path: '/admin', icon: Shield, show: true });
        items.push({ label: 'Artist Dashboard', path: '/artist-dashboard', icon: Calendar, show: true });
        items.push({ label: 'My Analytics', path: '/my-analytics', icon: BarChart3, show: true });
        items.push({ label: 'For You', path: '/for-you', icon: Sparkles, show: true });
      } else if (user.role === 'artist') {
        items.push({ label: 'Artist Dashboard', path: '/artist-dashboard', icon: Calendar, show: true });
      }

      // Add favorites
      items.push({ label: 'Favorites', path: '/favorites', icon: Heart, show: true });

      // Add chat - artists and admins only
      if (isOwnerOrOverseer || user.role === 'artist' || user.role === 'admin') {
        items.push({ label: 'Artist Chat', path: '/community-chat?room=artist', icon: MessageCircle, show: true });
      }

      // Add role-specific features
      if (user.role === 'artist') {
        items.push({ label: 'My Gallery', path: '/artist/gallery', icon: Image, show: true });
        items.push({ label: 'Technical Rider', path: '/artist/rider', icon: FileText, show: true });
        items.push({ label: 'Analytics', path: '/artist/analytics', icon: BarChart3, show: true });
        items.push({ label: 'Artist Settings', path: '/artist/settings', icon: Settings, show: true });
      } else {
        items.push({ label: 'Settings', path: '/profile/settings', icon: Settings, show: true });
      }
    }

    return items.filter(item => item.show);
  };

  const navItems = getNavItems();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy border-b border-white/10">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div 
            className="flex items-center gap-2 cursor-pointer flex-shrink-0"
            onClick={() => navigate('/')}
            data-testid="nav-logo"
          >
            <Music2 className="text-violet-400" size={28} />
            <span className="text-lg font-bold gradient-text hidden sm:block">RazientSounds</span>
          </div>

          {/* Right Side - Menu Button & Auth */}
          <div className="flex items-center gap-2">
            {/* Back to Site - Always visible on larger screens */}
            <Button
              data-testid="nav-back-to-site"
              onClick={() => window.location.href = 'https://razientsounds.com'}
              variant="ghost"
              size="sm"
              className="hidden sm:flex hover:bg-white/10 text-sm"
            >
              <ExternalLink size={16} className="mr-1.5" />
              Back to Site
            </Button>

            {/* Search */}
            <NavTip label="Search">
              <Button
                onClick={() => navigate('/search')}
                variant="ghost"
                size="sm"
                className="hidden sm:flex hover:bg-white/10"
                aria-label="Search"
              >
                <Search size={18} />
              </Button>
            </NavTip>

            {/* Theme Toggle */}
            <NavTip label="Light / Dark theme">
              <ThemeToggle />
            </NavTip>

            {/* Live — with live count badge */}
            <NavTip label="Live streams">
              <Button
                onClick={() => navigate('/live')}
                variant="ghost"
                size="sm"
                className="hover:bg-white/10 group relative"
                data-testid="nav-live"
                aria-label="Live streams"
              >
              <Radio
                size={18}
                className={`transition-all group-hover:scale-110 ${liveCount > 0 ? 'text-red-500' : 'text-white/60 group-hover:text-red-400'}`}
              />
              {liveCount > 0 && (
                <>
                  <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-600 rounded-full animate-ping opacity-60" />
                  <span
                    className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-600 rounded-full text-[10px] font-bold text-white flex items-center justify-center"
                    data-testid="nav-live-count"
                  >
                    {liveCount}
                  </span>
                </>
              )}
              </Button>
            </NavTip>

            {/* Nightclub Game */}
            <NavTip label="Virtual Nightclub">
              <Button
                onClick={() => navigate('/nightclub')}
                variant="ghost"
                size="sm"
                className="hover:bg-white/10 group relative"
                data-testid="nav-nightclub"
                aria-label="Virtual Nightclub"
              >
                <Gamepad2 
                  size={18} 
                  className="text-purple-400 group-hover:text-purple-300 transition-all group-hover:scale-110 group-hover:animate-pulse" 
                />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full animate-ping" />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
              </Button>
            </NavTip>

            {/* Messages - Show for authenticated users */}
            {isAuthenticated && (
              <NavTip label="Messages">
                <MessagesDropdown />
              </NavTip>
            )}

            {/* Notifications - Show for authenticated users */}
            {isAuthenticated && (
              <NavTip label="Notifications">
                <NotificationsDropdown />
              </NavTip>
            )}

            {/* Auth buttons for non-authenticated users */}
            {!isAuthenticated && (
              <div className="hidden sm:flex items-center gap-2">
                <Button
                  data-testid="nav-login"
                  onClick={() => navigate('/login')}
                  variant="ghost"
                  size="sm"
                  className="hover:bg-white/10"
                >
                  Sign In
                </Button>
                <Button
                  data-testid="nav-signup"
                  onClick={() => navigate('/signup')}
                  size="sm"
                  className="bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500"
                >
                  Sign Up
                </Button>
              </div>
            )}

            {/* User Avatar (authenticated) */}
            {isAuthenticated && user && (
              <div className="hidden sm:flex items-center">
                {user.picture ? (
                  <img 
                    src={user.picture} 
                    alt="Profile" 
                    className="w-8 h-8 rounded-full border-2 border-violet-500"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-violet-600 flex items-center justify-center text-sm font-medium">
                    {user.name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                )}
              </div>
            )}

            {/* Menu Button - Always visible */}
            <div className="relative" ref={menuRef}>
              <NavTip label="Menu">
                <Button
                  data-testid="menu-button"
                  onClick={() => setMenuOpen(!menuOpen)}
                  variant="ghost"
                  size="sm"
                  className="hover:bg-white/10 flex items-center gap-1"
                  aria-label="Menu"
                >
                  <Menu size={20} />
                  <ChevronDown size={14} className={`transition-transform ${menuOpen ? 'rotate-180' : ''}`} />
                </Button>
              </NavTip>

              {/* Dropdown Menu */}
              {menuOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-gray-950 border border-white/10 rounded-xl shadow-2xl overflow-hidden z-50">
                  {/* User info header (if authenticated) */}
                  {isAuthenticated && user && (
                    <div className="p-4 border-b border-white/10 bg-white/5">
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img 
                            src={user.picture} 
                            alt="Profile" 
                            className="w-10 h-10 rounded-full border-2 border-violet-500"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-violet-600 flex items-center justify-center text-lg font-medium">
                            {user.name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{user.name}</p>
                          <p className="text-xs text-white/60 capitalize">{user.role}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation Items */}
                  <div className="py-2 max-h-[60vh] overflow-y-auto">
                    {/* Back to Site - Mobile */}
                    <button
                      onClick={() => {
                        window.location.href = 'https://razientsounds.com';
                      }}
                      className="w-full px-4 py-2.5 text-left text-sm hover:bg-white/10 flex items-center gap-3 sm:hidden"
                    >
                      <ExternalLink size={18} className="text-white/60" />
                      Back to Site
                    </button>

                    {navItems.map((item, index) => (
                      <button
                        key={`${item.path}-${index}`}
                        onClick={() => handleNavigation(item.path)}
                        className={`w-full px-4 py-2.5 text-left text-sm hover:bg-white/10 flex items-center gap-3 ${
                          location.pathname === item.path || 
                          (item.path.includes('?') && location.pathname + location.search === item.path)
                            ? 'bg-violet-600/20 text-violet-400' 
                            : ''
                        }`}
                      >
                        <item.icon size={18} className="text-white/60" />
                        {item.label}
                        {item.path === '/live' && liveCount > 0 && (
                          <span className="ml-auto min-w-[18px] h-[18px] px-1 bg-red-600 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                            {liveCount}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Auth Section */}
                  <div className="border-t border-white/10 py-2">
                    {isAuthenticated ? (
                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-2.5 text-left text-sm hover:bg-white/10 flex items-center gap-3 text-red-400"
                      >
                        <LogOut size={18} />
                        Logout
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={() => handleNavigation('/login')}
                          className="w-full px-4 py-2.5 text-left text-sm hover:bg-white/10 flex items-center gap-3"
                        >
                          Sign In
                        </button>
                        <button
                          onClick={() => handleNavigation('/signup')}
                          className="w-full px-4 py-2.5 text-left text-sm hover:bg-white/10 flex items-center gap-3 text-violet-400"
                        >
                          Sign Up
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
